<script setup lang="ts">
/**
 * One line under the homepage hero that says what is free and what costs money,
 * so a visitor neither assumes it is all paid nor has to scroll to the price
 * list to find out. The full ladder is PricingTeaser (#pricing).
 */
const props = withDefaults(defineProps<{
  /** "Compare": the price list's anchor on this page, or a page of its own. */
  pricingTo?: string
}>(), {
  pricingTo: '#pricing',
})

const { t } = useI18n()
const localize = useLocalizedPath()
const { exportCreditPrice, jobPackPrice } = usePremiumPlans()
const { isAuthenticated } = useAuthUser()

/** "everything" opens what the pack holds; buying goes the way the price list sends it. */
const showPack = ref(false)
const paidTarget = computed(() => (isAuthenticated.value ? '/admin/premium' : '/registration'))

function toPricing() {
  showPack.value = false
  if (!props.pricingTo.startsWith('#')) {
    navigateTo(localize(props.pricingTo))
    return
  }
  nextTick(() => document.getElementById(props.pricingTo.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}

const plans = computed(() => [
  { id: 'free', price: t('homepage.planStrip.free.price'), text: t('homepage.planStrip.free.text') },
  { id: 'single', price: exportCreditPrice, text: t('homepage.planStrip.single.text') },
  { id: 'pack', price: t('homepage.planStrip.pack.price', { price: jobPackPrice }), text: t('homepage.planStrip.pack.text') },
])
</script>

<template>
  <!-- Phones: one plan per line; wider screens: one line with dots between -->
  <div class="sofax relative mx-auto max-w-[46rem] px-5 pb-10 text-sm sm:px-6 md:-mt-6">
    <ul class="flex flex-col items-center gap-1 text-center sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-2">
      <li v-for="(plan, index) in plans" :key="plan.id" class="!text-sm text-body dark:text-gray-300">
        <span v-if="index" class="mr-2 hidden text-muted sm:inline" aria-hidden="true">·</span>
        <span class="font-semibold text-ink dark:text-white">{{ plan.price }}: </span>
        <button
          v-if="plan.id === 'pack'"
          type="button"
          class="!text-sm font-semibold text-ink underline decoration-dotted underline-offset-4 hover:decoration-solid dark:text-white"
          @click="showPack = true"
        >
          {{ plan.text }}
        </button>
        <template v-else>{{ plan.text }}</template>
      </li>
      <li class="!text-sm">
        <LocaleLink
          :to="pricingTo"
          class="inline-flex items-center gap-1 font-semibold text-ink underline-offset-4 hover:underline dark:text-white"
        >
          <span class="sm:sr-only">{{ t('homepage.planStrip.compare') }}</span>
          <i class="bi bi-arrow-right" aria-hidden="true" />
        </LocaleLink>
      </li>
    </ul>

    <!-- The button sits in the dialog's footer, outside the scrolling body, so
         it stays in view on a phone where the list runs past the screen -->
    <CommonDialog v-model="showPack" size="lg" :header="false">
      <PremiumJobPackStack layout="offer" />

      <template #footer>
        <SofaxBtn variant="accent" block :to="paidTarget" :label="t('homepage.pricingTeaser.pack.cta')" />
        <p class="mt-2 text-center !text-sm">
          <button type="button" class="font-semibold text-ink underline-offset-4 hover:underline dark:text-white" @click="toPricing">
            {{ t('homepage.planStrip.compare') }}
          </button>
        </p>
      </template>
    </CommonDialog>
  </div>
</template>
